import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, map, Observable, of, startWith, Subject, switchMap } from 'rxjs';
import { MUNICIPALITIES_FILE, Municipality } from '../utils/municipality.utils';


@Component({
  selector: 'input-city',
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    TranslateModule
  ],
  templateUrl: './input-city.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputCity),
      multi: true,
    },
  ],
})
export class InputCity implements ControlValueAccessor, OnInit {
  @Input() required = false;
  @Input() readonly = false;

  control = new FormControl('');
  municipalities$: Subject<Municipality[]> = new Subject<Municipality[]>();
  filteredMunicipalities$!: Observable<Municipality[]>;
  private latestFilteredMunicipalities: Municipality[] = [];

  onChange: any = () => { };
  onTouched: any = () => { };

  constructor(private http: HttpClient) { }

  ngOnInit() {
    if (this.required) {
      this.control.setValidators(Validators.required);
    }

    this.initializeAutocomplete();
  }

  private initializeAutocomplete() {
    this.filteredMunicipalities$ = this.control.valueChanges.pipe(
      startWith(''),
      debounceTime(200),
      distinctUntilChanged(),
      switchMap((value: any) => {
        // Check if the field is empty or only whitespace
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          // Clear the selection and emit null
          this.onChange(null);
          this.latestFilteredMunicipalities = [];
          return of([]);
        }
        // start filtering only when input length >= 3
        else if (value && value.length >= 3) {
          return this.http.get<Municipality[]>(MUNICIPALITIES_FILE).pipe(
            map((municipalities: Municipality[]) => {
              const filtered = this.filterMunicipalities(municipalities, value || '');

              // If no municipalities match and the input is not empty, 
              // check if the current value is not a valid municipality name
              if (filtered.length === 0 && value.length >= 3) {
                const isValidMunicipality = municipalities.some(m =>
                  m.name.toLowerCase() === value.toLowerCase()
                );
                if (!isValidMunicipality) {
                  // User typed something invalid, clear the form value
                  this.onChange(null);
                }
              }

              return filtered;
            })
          );
        } else {
          return of([]);
        }
      })
    );
  }

  private filterMunicipalities(municipalities: Municipality[], value: string): Municipality[] {
    const filterValue = value.toLowerCase();
    const filtered = municipalities.filter((municipality) =>
      municipality.name.toLowerCase().startsWith(filterValue)
    );
    this.latestFilteredMunicipalities = filtered;
    return filtered;

  }

  writeValue(value: string): void {
    this.control.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (!isDisabled) {
      this.initializeAutocomplete();
    }
    isDisabled ? this.control.disable() : this.control.enable();
  }

  onMunicipalitySelected(event: MatAutocompleteSelectedEvent) {
    const selectedName = event.option.value
    const selectedMunicipality = this.latestFilteredMunicipalities.find(municipality => municipality.name === selectedName)

    if (selectedMunicipality) {
      this.control.setValue(selectedMunicipality.name);
      // Set CAP as the selected value
      this.onChange(selectedMunicipality.cap);
      this.onTouched();
    }
  }


  // An utility function to correctly display the selected value of the autocomplete
  displayMunicipality = (name: string) => {
    const found = this.latestFilteredMunicipalities.find(m => m.name === name);
    return found ? (found.name + ' (' + found.province + ')') : name;
  };
}
